import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Chip, MultiSelect, PriceBandIcon, SearchField, Table, Typography } from '@weareopenr/component-library';
import { createColumnHelper } from '@tanstack/react-table';
import { useAuthWrapper } from '@weareopenr/authkit';
import ActionsMenu from 'src/components/ActionsMenu';
import SkeletonContainer from 'src/components/SkeletonContainer';
import createAddressSummary from 'src/pages/Venue/createAddressSummary';
import { getPublishedMenusFormattedData } from 'src/pages/Venues/mapVenuesToTable/mapVenuesToTable';
import { useVenueCategories } from 'src/hooks/useVenueCategories';
import {
  parseCategoriesParam,
  serializeCategoriesParam,
  venueCategoriesToSelectOptions,
  venuesInSelectedCategories,
} from 'src/utils/venueCategories/mappedVenueCategories';
import { IVenue, VenuesTabProps } from './types';
import styles from './VenuesTab.module.scss';

const columnHelper = createColumnHelper<IVenue>();

const renderActionsCell = (
  venueId: string,
  onView: (venueId: string) => void,
  handleOpenDeletePrompt: (id: string) => void,
) => {
  return <ActionsMenu onView={() => onView(venueId)} onDelete={() => handleOpenDeletePrompt(venueId)} />;
};

const renderNameCell = (name: string, isTrial: boolean) => {
  return (
    <div className={styles['name-container']}>
      <Typography>{name}</Typography>
      {isTrial && <Chip label="Trial" backgroundColour="grape" />}
    </div>
  );
};

const VenuesTab = ({ venues, handleOpenDeletePrompt, isSyncInProgress = false }: VenuesTabProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchValue, setSearchValue] = useState<string>('');

  const { featureFlags } = useAuthWrapper();
  const hasVenueCategories = featureFlags.includes('venue_categories');
  const {
    nestedCategories,
    data,
    isLoading: isCategoriesLoading,
  } = useVenueCategories({
    enabled: hasVenueCategories,
  });

  const categoryOptions = useMemo(() => venueCategoriesToSelectOptions(nestedCategories), [nestedCategories]);

  const [selectedCategories, setSelectedCategories] = useState<string[]>(() =>
    hasVenueCategories ? parseCategoriesParam(new URLSearchParams(location.search)) : [],
  );

  const handleCategoryChange = useCallback(
    (selected: string[]): void => {
      setSelectedCategories(selected);

      const search = serializeCategoriesParam(selected, window.location.search);
      const url = `${location.pathname}${search ? `?${search}` : ''}${location.hash}`;
      window.history.replaceState(window.history.state, '', url);
    },
    [location.pathname, location.hash],
  );

  const venuesData: IVenue[] = useMemo(
    () =>
      (venues ?? [])
        .map((venue) => ({
          venueId: venue.venue_id,
          name: venue.name,
          isTrial: venue.is_trial,
          creationDate: venue.creation_date,
          address: createAddressSummary(venue.addresses[0]),
          publishedChannels: venue.published_channels,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [venues],
  );

  const filteredVenues: IVenue[] = useMemo(() => {
    let result = venuesData;

    if (selectedCategories.length > 0) {
      const venueIds = new Set(venuesInSelectedCategories(selectedCategories, data?.categories ?? []));
      result = result.filter((venue) => venueIds.has(venue.venueId));
    }

    const query = searchValue.trim().toLowerCase();
    if (query) {
      result = result.filter((venue) => venue.name.toLowerCase().includes(query));
    }

    return result;
  }, [venuesData, selectedCategories, data?.categories, searchValue]);

  const onView = useCallback(
    (venueId: string): void => {
      navigate(`/venues/${venueId}`);
    },
    [navigate],
  );

  const handleRowClick = useCallback(
    (rowData: IVenue) => {
      return () => onView(rowData.venueId);
    },
    [onView],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => renderNameCell(info.getValue(), info.row.original.isTrial),
      }),
      columnHelper.accessor('address', {
        header: 'Address',
      }),
      columnHelper.accessor('publishedChannels', {
        header: 'Menus per channel',
        cell: (info) => getPublishedMenusFormattedData(info.getValue() || []),
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => renderActionsCell(info.row.original.venueId, onView, handleOpenDeletePrompt),
        size: 80,
      }),
    ],
    [handleOpenDeletePrompt, onView],
  );

  if (isSyncInProgress) return <SkeletonContainer hiddenTitle />;

  return (
    <div className={styles['venues-tab']}>
      <div className={styles['controls-row']}>
        <SearchField
          placeholder="Search"
          onChange={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setSearchValue(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          isDisabled={false}
        />
        {hasVenueCategories && (
          <MultiSelect
            Icon={PriceBandIcon}
            label="Venue categories"
            options={categoryOptions}
            selected={selectedCategories}
            onChange={handleCategoryChange}
            loading={isCategoriesLoading}
            disabled={categoryOptions.length === 0}
          />
        )}
      </div>
      <Table
        className={styles['table-fill-height']}
        columns={columns}
        data={filteredVenues}
        isRowClickable={handleRowClick}
      />
    </div>
  );
};

export default VenuesTab;
